import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import axios from 'axios';

const VendingGrid = ({ layout = [], machine_uid, max_col }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const res = await axios.get('https://app.zts-inc.com/zts/admin/v1.0/product/');
      setProducts(res.data || []);
    } catch (err) {
      console.error('Failed to fetch products', err);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleChangeProduct = async () => {
    if (!selectedItem || !selectedProduct) return;

    const payload = {
      item: selectedProduct,
      machine_uid: machine_uid,
      compartment: `${selectedItem.row}${selectedItem.col}`,
    };

    try {
      if (selectedItem.id) {
        // Update existing item
        await axios.put(`https://app.zts-inc.com/zts/admin/v1.0/item/${selectedItem.id}/`, {
          ...selectedItem,
          item: selectedProduct,
        });
      } else {
        // Assign new item via POST
        await axios.post(`https://app.zts-inc.com/zts/admin/v1.0/item/`, payload);
      }

      setSnackbarMessage('Product saved successfully!');
      setDialogOpen(false);
    } catch (err) {
      console.error('Failed to save product', err);
      setSnackbarMessage('Failed to save product');
    }
  };

  if (!layout || layout.length === 0) {
    return <Typography>No layout data available.</Typography>;
  }

  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6" align="center" sx={{ mb: 3 }}>
        Machine UID: {machine_uid}
      </Typography>

      {layout.map((row, rowIndex) => {
        const { product } = row;
        const row_col_count = Math.max(...product.map((p) => (p.col || 1) + (p.col_span || 1) - 1));
        const slots = Array.from({ length: row_col_count }, () => null);

        product.forEach((item) => {
          const colIndex = item.col - 1;
          const span = item.col_span || 1;

          if (colIndex < max_col) {
            slots[colIndex] = { ...item, _span: span };
            for (let i = 1; i < span; i++) {
              if (colIndex + i < max_col) {
                slots[colIndex + i] = '__SKIP__';
              }
            }
          }
        });

        return (
          <Grid container spacing={2} key={`row-${rowIndex}`} sx={{ mb: 2 }}>
            {slots.map((slot, colIdx) => {
              if (slot === '__SKIP__') return null;

              const colSpan = slot?._span || 1;
              const xs = Math.floor((12 * colSpan) / max_col);

              const openDialog = (slotData, isEmpty = false) => {
                setSelectedItem(
                  isEmpty
                    ? { id: null, col: colIdx + 1, row: rowIndex }
                    : { ...slotData, row: rowIndex }
                );
                setSelectedProduct('');
                setDialogOpen(true);
                fetchProducts();
              };

              if (!slot || (!slot.name && !slot.image_url)) {
                return (
                  <Grid item xs={xs} key={`empty-${colIdx}`}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        border: '1px dashed #aaa',
                        backgroundColor: '#f0f0f0',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 2,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Empty Slot
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1 }}
                        onClick={() => openDialog(null, true)}
                      >
                        Assign Product
                      </Button>
                    </Card>
                  </Grid>
                );
              }

              const isOutOfStock = slot.balance === '0';
              const hasImage = !!slot.image_url;

              return (
                <Grid item xs={xs} key={`product-${colIdx}`}>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        borderRadius: '4px',
                        px: 0.5,
                        fontSize: '0.75rem',
                        zIndex: 1,
                      }}
                    >
                      {rowIndex}
                      {slot.col}
                    </Box>

                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        borderColor: isOutOfStock ? 'error.main' : 'grey.300',
                        opacity: isOutOfStock ? 0.5 : 1,
                        boxShadow: 1,
                        transition: '0.2s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.03)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      {hasImage && (
                        <CardMedia
                          component="img"
                          height="100"
                          image={slot.image_url}
                          alt={slot.name || ''}
                          sx={{
                            objectFit: 'contain',
                            p: 1,
                            mx: 'auto',
                            width: 'auto',
                            maxHeight: 100,
                          }}
                        />
                      )}
                      <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" noWrap>
                          {slot.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {slot.price}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          Balance: {slot.balance} / {slot.max_quantity}
                        </Typography>
                        {isOutOfStock && (
                          <Chip label="OUT OF STOCK" size="small" color="error" sx={{ mt: 1 }} />
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          sx={{ mt: 1 }}
                          onClick={() => openDialog(slot)}
                        >
                          Change Product
                        </Button>
                      </CardContent>
                    </Card>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        );
      })}

      {/* Dialog to select new product */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth>
        <DialogTitle>Select Product</DialogTitle>
        <DialogContent>
          {loadingProducts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Select
              fullWidth
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              displayEmpty
              sx={{ mt: 2 }}
            >
              <MenuItem value="" disabled>
                Select product
              </MenuItem>
              {products.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.name}
                </MenuItem>
              ))}
            </Select>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleChangeProduct}
            disabled={!selectedProduct}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default VendingGrid;



// import React from 'react';
// import {
//   Box,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   CardMedia,
//   Chip,
// } from '@mui/material';

// const VendingGrid = ({ layout = [], machine_uid, max_col }) => {
//   if (!layout || layout.length === 0) {
//     return <Typography>No layout data available.</Typography>;
//   }

//   return (
//     <Box sx={{ my: 3 }}>
//       <Typography variant="h6" align="center" sx={{ mb: 3 }}>
//         Machine UID: {machine_uid}
//       </Typography>

//       {layout.map((row, rowIndex) => {
//           // const { product } = row;
//           // const slots = Array.from({ length: max_col }, () => null);
//           const { product } = row;
//           const row_col_count = Math.max(product.map((p) => (p.col || 1) + (p.col_span || 1) - 1));
//           const slots = Array.from({ length: row_col_count }, () => null);

//         product.forEach((item) => {
//           const colIndex = item.col - 1;
//           const span = item.col_span || 1;

//           if (colIndex < max_col) {
//             slots[colIndex] = { ...item, _span: span };
//             for (let i = 1; i < span; i++) {
//               if (colIndex + i < max_col) {
//                 slots[colIndex + i] = '__SKIP__';
//               }
//             }
//           }
//         });

//         return (
//           <Grid container spacing={2} key={`row-${rowIndex}`} sx={{ mb: 2 }}>
//             {slots.map((slot, colIdx) => {
//               if (slot === '__SKIP__') return null;

//               const colSpan = slot?._span || 1;
//               const xs = Math.floor((12 * colSpan) / max_col);

//               if (!slot || (!slot.name && !slot.image_url)) {
//                 return (
//                   <Grid item xs={xs} key={`empty-${colIdx}`}>
//                     <Box
//                       sx={{
//                         height: 180,
//                         border: '1px dashed #ccc',
//                         borderRadius: 1,
//                         backgroundColor: '#f8f8f8',
//                       }}
//                     />
//                   </Grid>
//                 );
//               }

//               const isOutOfStock = slot.balance === '0';
//               const hasImage = !!slot.image_url;

//               return (
//               <Grid item xs={xs} key={`product-${colIdx}`}>
//                 <Box sx={{ position: 'relative' }}>
//                   <Box
//                     sx={{
//                       position: 'absolute',
//                       top: 6,
//                       right: 6,
//                       backgroundColor: 'rgba(0,0,0,0.7)',
//                       color: 'white',
//                       borderRadius: '4px',
//                       px: 0.5,
//                       fontSize: '0.75rem',
//                       zIndex: 1,
//                     }}
//                   >
//                     {rowIndex}{slot.col}
//                   </Box>

//                   <Card
//                     variant="outlined"
//                     sx={{
//                       height: '100%',
//                       borderColor: isOutOfStock ? 'error.main' : 'grey.300',
//                       opacity: isOutOfStock ? 0.5 : 1,
//                       boxShadow: 1,
//                       transition: '0.2s ease-in-out',
//                       '&:hover': {
//                         transform: 'scale(1.03)',
//                         boxShadow: 4,
//                       },
//                     }}
//                   >
//                     {hasImage && (
//                       <CardMedia
//                         component="img"
//                         height="100"
//                         image={slot.image_url}
//                         alt={slot.name || ''}
//                         sx={{
//                           objectFit: 'contain',
//                           p: 1,
//                           mx: 'auto',
//                           width: 'auto',
//                           maxHeight: 100,
//                         }}
//                       />
//                     )}
//                     <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1 }}>
//                       <Typography
//                         variant="subtitle2"
//                         fontWeight="bold"
//                         noWrap
//                       >
//                         {slot.name}
//                       </Typography>
//                       <Typography variant="body2" color="text.secondary">
//                         {slot.price}
//                       </Typography>
//                       <Typography
//                         variant="caption"
//                         color="text.secondary"
//                         sx={{ display: 'block', mt: 0.5 }}
//                       >
//                         Balance: {slot.balance} / {slot.max_quantity}
//                       </Typography>
//                       {isOutOfStock && (
//                         <Chip
//                           label="OUT OF STOCK"
//                           size="small"
//                           color="error"
//                           sx={{ mt: 1 }}
//                         />
//                       )}
//                     </CardContent>
//                   </Card>
//                   </Box>
//                 </Grid>
//               );
//             })}
//           </Grid>
//         );
//       })}
//     </Box>
//   );
// };

// export default VendingGrid;





